<template>
  <div class="layout-components">
    <div class="content">
      <div class="side" :class="ISCOLLAPSE ? 'hideSidebar' : 'openSidebar'">
        <left-bar />
      </div>
      <div
        class="view-content main-content"
        :class="ISCOLLAPSE ? 'hideSidebar' : 'openSidebar'"
      >
        <!-- 服务到期提醒 -->
        <el-alert
          v-if="showServiceAlert && !isServiceAlertClosedToday"
          class="layout-components__alert"
          :title="serviceAlertTitle"
          type="warning"
          show-icon
          closable
          @close="handleServiceAlertClose"
        >
        </el-alert>
        <!-- 余额不足提醒 -->
        <el-alert
          v-if="showAlert && !isAlertClosedToday"
          class="layout-components__alert"
          :title="alertTitle"
          type="warning"
          show-icon
          closable
          @close="handleAlertClose"
        >
        </el-alert>
        <router-view />
      </div>
    </div>
  </div>
</template>
<script>
import leftBar from '../layouts/LeftBar'
import { mapState, mapGetters } from 'vuex'
export default {
  name: 'Layout',
  components: {
    leftBar
  },
  data() {
    return {
      isAlertClosedToday: false, // 今天是否已关闭警告
      isServiceAlertClosedToday: false // 今天是否已关闭服务到期警告
    }
  },
  computed: {
    ...mapGetters(['isMedicalAssociationEnv']),
    ...mapGetters('micro/magazine', ['MAGAZINE_INFO']),
    ...mapState('micro/sidebar', { ISCOLLAPSE: 'isCollapse' }),
    ...mapState('incrementService', {
      showAlert: 'showMailBalanceAlert'
    }),
    ...mapGetters('incrementService', ['getServiceValidUntil']),
    alertTitle() {
      let currentDate = new Date()
      let currentYear = currentDate.getFullYear()
      return `截至${currentYear}年${currentDate.getMonth() +
        1}月${currentDate.getDate()}日，《${this.MAGAZINE_INFO?.title ||
        ''}》的邮件发送额度已不足2000封，请及时处理，否则影响邮件的正常推送。`
    },
    // 是否显示服务到期提醒
    showServiceAlert() {
      const serviceValidUntil = this.getServiceValidUntil
      if (!serviceValidUntil) return false

      const expiryDate = new Date(serviceValidUntil)
      const currentDate = new Date()
      const daysUntilExpiry = Math.ceil(
        (expiryDate - currentDate) / (1000 * 60 * 60 * 24)
      )

      // 到期前7天开始提醒
      return daysUntilExpiry <= 7 && daysUntilExpiry >= 0
    },
    serviceAlertTitle() {
      const serviceValidUntil = this.getServiceValidUntil
      return `《${this.MAGAZINE_INFO?.title ||
        ''}》邮件推送服务即将于${serviceValidUntil}到期，如需继续使用，请尽快续费。`
    }
  },
  watch: {
    isMedicalAssociationEnv: {
      async handler(nv) {
        if (nv) {
          // 应用初始化时获取邮箱余额，用于判断是否显示警告
          await this.$store.dispatch('incrementService/fetchMailBalance')
          // 应用初始化时获取发布同步状态
          await this.$store.dispatch('incrementService/fetchIsPubSync')
        }
      },
      immediate: true
    }
  },
  async mounted() {
    // 检查今天是否已经关闭过警告
    this.checkAlertClosedStatus()
    // 检查今天是否已经关闭过服务到期警告
    this.checkServiceAlertClosedStatus()
    // 全局刷新 Element UI 弹层位置
    // 问题：Element UI v2 的 popper 默认只监听 window scroll，
    // 但项目有多层自定义滚动容器（Home.view-content / email.page-content / dataOverview.new-data-overview）
    // scroll 事件不冒泡，capture 也不传到 document，所以必须监听 window + 所有可滚动容器
    // 且 .el-picker-panel.__vue__ 是面板子组件没有 popperJS，只有 .el-popper.__vue__ 才是组件本身
    this._refreshHandler = () => this.refreshPoppers()
    // 1. window 级别
    window.addEventListener('scroll', this._refreshHandler, true)
    // 2. 全局遍历所有可滚动容器
    this.$nextTick(() => {
      this.bindScrollableContainers()
    })
    // 3. DOM 变化时重新绑定（路由切换等场景）
    this._mo = new MutationObserver(() => {
      this.bindScrollableContainers()
    })
    this._mo.observe(document.body, { childList: true, subtree: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this._refreshHandler, true)
    if (this._mo) this._mo.disconnect()
    this._boundContainers &&
      this._boundContainers.forEach((el) =>
        el.removeEventListener('scroll', this._refreshHandler, true)
      )
  },
  methods: {
    // 遍历 Vue 组件树，找所有持有 popperJS 实例的组件并 update
    _walkAndUpdate(vm) {
      if (!vm) return
      // Element UI v2 VuePopper mixin 的 popperJS 实例
      if (vm.popperJS && typeof vm.popperJS.update === 'function') {
        vm.popperJS.update()
      }
      if (vm.$children && vm.$children.length) {
        vm.$children.forEach(this._walkAndUpdate)
      }
    },
    // 节流：一帧内只刷一次
    refreshPoppers() {
      if (this._raf) return
      this._raf = requestAnimationFrame(() => {
        this._raf = null
        if (this.$root) this._walkAndUpdate(this.$root)
      })
    },
    // 找出所有 overflow: auto/scroll 的元素，绑 scroll 监听
    bindScrollableContainers() {
      const selector =
        '[style*="overflow: auto"], [style*="overflow-y: auto"], [style*="overflow-x: auto"], ' +
        '[style*="overflow: scroll"], [style*="overflow-y: scroll"], ' +
        '.view-content, .page-content, .new-data-overview, .content-area'
      const els = document.querySelectorAll(selector)
      this._boundContainers = this._boundContainers || new Set()
      els.forEach((el) => {
        if (!this._boundContainers.has(el)) {
          el.addEventListener('scroll', this._refreshHandler, true)
          this._boundContainers.add(el)
        }
      })
    },
    // 检查今天是否已经关闭过警告
    checkAlertClosedStatus() {
      const closedDate = localStorage.getItem('mailBalanceAlertClosedDate')
      const today = this.getTodayDate()
      // 如果存储的日期不是今天，则重置状态
      if (closedDate !== today) {
        this.isAlertClosedToday = false
      } else {
        this.isAlertClosedToday = true
      }
    },
    // 检查今天是否已经关闭过服务到期警告
    checkServiceAlertClosedStatus() {
      const closedDate = localStorage.getItem('serviceAlertClosedDate')
      const today = this.getTodayDate()
      // 如果存储的日期不是今天，则重置状态
      if (closedDate !== today) {
        this.isServiceAlertClosedToday = false
      } else {
        this.isServiceAlertClosedToday = true
      }
    },
    // 关闭警告时保存今天的日期
    handleAlertClose() {
      const today = this.getTodayDate()
      localStorage.setItem('mailBalanceAlertClosedDate', today)
      this.isAlertClosedToday = true
    },
    // 关闭服务到期警告时保存今天的日期
    handleServiceAlertClose() {
      const today = this.getTodayDate()
      localStorage.setItem('serviceAlertClosedDate', today)
      this.isServiceAlertClosedToday = true
    },
    // 获取今天的日期字符串 (格式: YYYY-MM-DD)
    getTodayDate() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>
<style lang="scss">
.layout-components {
  position: relative;
  top: 0px;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  &__alert {
    background-color: #faeaea;
    color: #ff0000c4;
    display: flex;
    justify-content: center;
  }

  .content {
    $side-width: 190px;
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    text-align: left;
    height: calc(100vh - 70px);
    .side,
    .view-content {
      position: absolute;
      top: 0;
      bottom: 0;
      overflow-y: auto;
    }
    .side {
      left: 0;
      overflow: hidden;
      &.hideSidebar {
        width: 60px;
      }
      &.openSidebar {
        width: $side-width;
      }
    }
    .view-content {
      right: 0;
      padding-right: 0;
      overflow: auto;
      &.hideSidebar {
        left: 60px;
      }
      &.openSidebar {
        left: $side-width - 1px;
      }
    }
  }
}
</style>
<style>
.layout-components__alert .el-alert__content {
  text-align: center;
  /* width: 100%; */
}
</style>
